// Shared section rendering for any book — used by both the Current Book page
// and the Book Detail page. Renders all AI-generated content sections plus
// chapter notes.
//
// Each section is wrapped in <section id="..." className="scroll-mt-24"> so
// the SectionNav can deep-link and the section header clears the sticky
// page header when scrolled to. When adding new sections, keep the id list
// in sync with the parent page's SectionNav sections array.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  api,
  type AuthorContext,
  type ChapterDescription,
  type ChapterQuestions,
  type Character,
  type FoodDrinkPairing,
  type KeyPassage,
  type RelationshipGraph as RG,
  type Theme,
  type VenueSuggestion,
} from "../api";
import { Card, Ornament, Badge } from "./ui";
import { AISection, type CustomEditorProps } from "./AISection";
import { AuthorContextSection } from "./AuthorContextSection";
import { KeyPassagesSection } from "./KeyPassagesSection";
import { DiscussionQuestionsSection } from "./DiscussionQuestionsSection";
import { ChapterNotesSection } from "./ChapterNotesSection";
import { RelationshipGraph } from "./RelationshipGraph";
import { MerchSection } from "./MerchSection";

interface Props {
  bookId: number;
}

export function BookSections({ bookId }: Props) {
  const chaptersCache = useQuery({
    queryKey: ["ai", bookId, "chapters"],
    queryFn: () =>
      api.get<{ content: { type: "chapters"; data: ChapterDescription[]; generatedAt: string } | null }>(
        `/ai/${bookId}/chapters`
      ),
  });
  const chaptersData = chaptersCache.data?.content?.data ?? null;

  return (
    <>
      <section id="section-context" className="scroll-mt-24">
        <AISection<AuthorContext>
          bookId={bookId}
          type="context"
          eyebrow="Setting the stage"
          title="The author and the moment"
          emptyCta="Generate context"
          render={(data) => <AuthorContextSection data={data} />}
        />
      </section>

      <Ornament />

      <section id="section-themes" className="scroll-mt-24">
        <AISection<Theme[]>
          bookId={bookId}
          type="themes"
          eyebrow="What we'll talk about"
          title="Themes worth a glass of wine"
          emptyCta="Find the themes"
          customEditor={(props) => <ThemesOverrideEditor {...props} />}
          render={(themes) => (
            <SpoilerCurtain
              label="Themes Worth a Glass of Wine"
              hint="Hidden until you're ready — these touch on what the book is really up to."
            >
              <div className="grid md:grid-cols-2 gap-7 mt-2">
                {themes.map((t, i) => (
                  <article key={i} className="flex flex-col gap-2.5">
                    <div className="flex items-center gap-3">
                      <span className="text-gold font-serif italic text-2xl">{i + 1}.</span>
                      <h3 className="font-serif text-xl text-plum">{t.title}</h3>
                    </div>
                    <p className="text-plum-soft leading-relaxed">{t.description}</p>
                  </article>
                ))}
              </div>
            </SpoilerCurtain>
          )}
        />
      </section>

      <section id="section-passages" className="scroll-mt-24">
        <AISection<KeyPassage[]>
          bookId={bookId}
          type="passages"
          eyebrow="Read aloud"
          title="Passages worth lingering on"
          description="Two or three lines that deserve a moment at the table."
          emptyCta="Find key passages"
          render={(passages) => (
            <SpoilerCurtain
              label="Passages Worth Lingering On"
              hint="Specific lines — open when you've reached them."
            >
              <KeyPassagesSection passages={passages} />
            </SpoilerCurtain>
          )}
        />
      </section>

      <section id="section-chapters" className="scroll-mt-24">
        <AISection<ChapterDescription[]>
          bookId={bookId}
          type="chapters"
          eyebrow="Reading guide"
          title="Chapter by chapter"
          description="A spoiler-aware walk through what happens, so the conversation has a backbone."
          emptyCta="Generate chapter guide"
          render={(chapters) => (
            <SpoilerCurtain
              label="Chapter by Chapter"
              hint="The full walk-through. Once you open it, each chapter is still its own tab — click only what you've read."
            >
              <ChaptersWalkthrough chapters={chapters} />
            </SpoilerCurtain>
          )}
        />
      </section>

      <section id="section-notes" className="scroll-mt-24">
        <Card accent>
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex flex-col gap-2">
              <span className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-gold">
                The conversation
              </span>
              <h2 className="font-serif text-3xl text-plum tracking-tight">Chapter notes</h2>
            </div>
          </div>
          <ChapterNotesSection bookId={bookId} chapters={chaptersData} />
        </Card>
      </section>

      <section id="section-questions" className="scroll-mt-24">
        <AISection<ChapterQuestions[]>
          bookId={bookId}
          type="questions"
          eyebrow="For the table"
          title="Discussion questions"
          description="Open-ended prompts, organized by chapter. Drag the spoiler control to limit what's shown."
          emptyCta="Generate questions"
          customEditor={(props) => <QuestionsOverrideEditor {...props} />}
          render={(chapters) => (
            <DiscussionQuestionsSection bookId={bookId} chapters={chapters} />
          )}
        />
      </section>

      <section id="section-characters" className="scroll-mt-24">
        <AISection<Character[]>
          bookId={bookId}
          type="characters"
          eyebrow="Cast"
          title="Who's who"
          description="The characters worth keeping straight."
          emptyCta="Build the character list"
          render={(characters) => (
            <div className="grid md:grid-cols-2 gap-5">
              {characters.map((p) => (
                <div key={p.name} className="flex flex-col gap-1.5 p-5 rounded-xl bg-cream-50/70 border border-cream-200">
                  <div className="flex items-baseline justify-between gap-3">
                    <h4 className="font-serif text-lg text-plum">{p.name}</h4>
                    <span className="text-[0.7rem] uppercase tracking-[0.14em] text-gold-dark italic">
                      {p.role}
                    </span>
                  </div>
                  <p className="text-plum-soft text-sm leading-relaxed">{p.description}</p>
                </div>
              ))}
            </div>
          )}
        />
      </section>

      <section id="section-relationships" className="scroll-mt-24">
        <AISection<RG>
          bookId={bookId}
          type="relationships"
          eyebrow="Constellations"
          title="How they're connected"
          description="The lines between characters, drawn in gold and rose."
          emptyCta="Map the relationships"
          render={(graph) => <RelationshipGraph graph={graph} />}
        />
      </section>

      <section id="section-venue" className="scroll-mt-24">
        <AISection<VenueSuggestion>
          bookId={bookId}
          type="venue"
          eyebrow="Where we'll gather"
          title="A venue that fits"
          description="Somewhere in central New Jersey whose vibe matches the book."
          emptyCta="Suggest a venue"
          customEditor={(props) => <VenueOverrideEditor {...props} />}
          render={(venue) => {
            const mapsHref = venue.address
              ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`
              : venue.lat && venue.lng
              ? `https://www.google.com/maps/search/?api=1&query=${venue.lat},${venue.lng}`
              : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${venue.name}, ${venue.town}, NJ`)}`;
            const isFallback = venue.source === "ai_fallback" || !venue.source;
            return (
              <div className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <h3 className="font-serif text-2xl text-plum">{venue.name}</h3>
                  <Badge tone="rose">{venue.type}</Badge>
                </div>
                <p className="text-plum-soft text-sm font-medium tracking-wide uppercase">
                  {venue.town}{!venue.town.toLowerCase().endsWith("nj") ? ", NJ" : ""}
                </p>
                {venue.address ? (
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-rose text-sm underline w-fit"
                  >
                    {venue.address}
                  </a>
                ) : (
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-rose text-xs underline w-fit"
                  >
                    Search on Google Maps
                  </a>
                )}
                <p className="text-plum leading-relaxed italic font-serif mt-1">"{venue.whyItFits}"</p>
                {isFallback ? (
                  <p className="text-[0.7rem] text-plum-mute italic mt-2">
                    No real-world match in our local database — this is a suggested vibe / town. Edit the section to set a specific venue.
                  </p>
                ) : (
                  <p className="text-[0.65rem] text-plum-mute uppercase tracking-[0.16em] mt-2">
                    Real venue · pulled from {venue.source === "google" ? "Google Places" : "OpenStreetMap"}
                  </p>
                )}
              </div>
            );
          }}
        />
      </section>

      <section id="section-merch" className="scroll-mt-24">
        <MerchSection bookId={bookId} />
      </section>

      <section id="section-pairings" className="scroll-mt-24">
        <AISection<FoodDrinkPairing>
          bookId={bookId}
          type="pairings"
          eyebrow="The hosting"
          title="Food & drink pairings"
          description="Bites and pours that lean into the book's spirit."
          emptyCta="Pair the menu"
          render={(p) => (
            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <h4 className="font-serif text-lg text-plum mb-4 flex items-center gap-2">
                  <span className="text-gold">●</span> On the table
                </h4>
                <ul className="flex flex-col gap-4">
                  {p.food.map((f) => (
                    <li key={f.name}>
                      <div className="font-serif text-base text-plum">{f.name}</div>
                      <p className="text-plum-soft text-sm leading-relaxed mt-0.5">{f.description}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-serif text-lg text-plum mb-4 flex items-center gap-2">
                  <span className="text-rose">●</span> In the glass
                </h4>
                <ul className="flex flex-col gap-5">
                  {p.drinks.map((d) => (
                    <li key={d.name} className="flex flex-col gap-1.5">
                      <div className="font-serif text-base text-plum">{d.name}</div>
                      <p className="text-plum-soft text-sm leading-relaxed">{d.description}</p>
                      <p className="mt-1 px-3 py-2 rounded-lg bg-blush-soft/40 text-plum-light text-xs leading-relaxed font-mono">
                        {d.recipe}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        />
      </section>
    </>
  );
}

// ─── Spoiler curtain ──────────────────────────────────────────────────────
// Hides a section's body behind a click-to-reveal tab. Designed for the
// sections that contain real spoilers (themes, passages). Stays revealed
// for the session once opened, with a discreet "Hide again" affordance.

function SpoilerCurtain({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  const [revealed, setRevealed] = useState(false);
  if (revealed) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setRevealed(false)}
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-mute hover:text-bordeaux px-2 py-1"
          >
            ← Hide again
          </button>
        </div>
        {children}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setRevealed(true)}
      className="group w-full py-12 px-6 border border-rule hover:border-bordeaux bg-rose-paper-2/30 hover:bg-rose-paper-2/60 transition-colors flex flex-col items-center gap-2 text-center"
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-champagne group-hover:text-bordeaux">
        Spoilers ahead — click to reveal
      </span>
      <span className="font-serif italic text-2xl text-plum group-hover:text-bordeaux">
        {label}
      </span>
      {hint ? (
        <span className="text-xs text-plum-mute max-w-md leading-relaxed">{hint}</span>
      ) : null}
    </button>
  );
}

// ─── Chapter walk-through with per-chapter tabs ───────────────────────────
// The chapter list is the most spoiler-heavy section. Replaces the
// always-visible numbered list with a strip of chapter tabs — one click,
// one chapter revealed. Nothing is shown until a tab is selected.

function ChaptersWalkthrough({ chapters }: { chapters: ChapterDescription[] }) {
  const [openChapter, setOpenChapter] = useState<number | null>(null);
  const active = openChapter !== null ? chapters.find((c) => c.number === openChapter) : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-2">
        {chapters.map((ch) => {
          const isOpen = openChapter === ch.number;
          return (
            <button
              key={ch.number}
              type="button"
              onClick={() => setOpenChapter(isOpen ? null : ch.number)}
              className={[
                "font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-colors",
                isOpen
                  ? "bg-bordeaux text-rose-paper border-bordeaux"
                  : "border-rule text-ink-mute hover:border-bordeaux hover:text-bordeaux",
              ].join(" ")}
              title={ch.title ?? `Chapter ${ch.number}`}
            >
              Ch. {ch.number}
            </button>
          );
        })}
      </div>

      {active ? (
        <article className="flex gap-5 pt-2 border-t border-rule pt-6">
          <span className="font-serif italic text-rose text-3xl shrink-0 w-12 text-center pt-0.5">
            {active.number}
          </span>
          <div className="flex-1">
            {active.title ? (
              <h4 className="font-serif text-xl text-plum mb-2">{active.title}</h4>
            ) : null}
            <p className="text-plum-soft leading-relaxed">{active.description}</p>
          </div>
        </article>
      ) : (
        <p className="text-sm text-plum-mute italic font-serif border-t border-rule pt-5">
          Click a chapter above to reveal its walk-through. Stays quiet until you ask.
        </p>
      )}
    </div>
  );
}

// Form-based override editor for the book's "venue that fits" section.
// Replaces the generic JSON editor — members shouldn't have to hand-edit
// JSON to swap a venue.
function VenueOverrideEditor({
  bookId,
  initial,
  onClose,
  onSaved,
}: CustomEditorProps<VenueSuggestion>) {
  const [name, setName] = useState(initial?.name ?? "");
  const [town, setTown] = useState(initial?.town ?? "");
  const [venueType, setVenueType] = useState(initial?.type ?? "");
  const [address, setAddress] = useState(initial?.address ?? "");
  const [whyItFits, setWhyItFits] = useState(initial?.whyItFits ?? "");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      if (!name.trim()) throw new Error("Venue name is required.");
      const payload: VenueSuggestion = {
        name: name.trim(),
        town: town.trim() || "Central NJ",
        type: venueType.trim() || "venue",
        whyItFits: whyItFits.trim(),
        address: address.trim() || null,
        lat: null,
        lng: null,
        source: "ai_fallback",
      };
      return api.post(`/annotations/${bookId}/venue/override`, {
        data: payload,
        reason: reason.trim() || null,
      });
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 bg-plum/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-cream-50 rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-cream-200">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-gold mb-0.5">
              Set a venue
            </div>
            <h3 className="font-serif text-xl text-plum">Where we'll gather</h3>
          </div>
          <button
            onClick={onClose}
            className="text-plum-soft hover:text-rose p-2 -mr-2 rounded-full hover:bg-cream-100"
            aria-label="Close editor"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4 overflow-y-auto">
          <p className="text-sm text-plum-soft leading-relaxed">
            Override the AI's pick with your own choice. The AI version is kept and you can
            reset to it from the section menu.
          </p>

          <Field label="Venue name" required>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null); }}
              placeholder="e.g. Witherspoon Grill"
              autoFocus
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-cream-300 text-plum text-sm placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Town">
              <input
                type="text"
                value={town}
                onChange={(e) => setTown(e.target.value)}
                placeholder="Princeton"
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-cream-300 text-plum text-sm placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
              />
            </Field>
            <Field label="Type">
              <input
                type="text"
                value={venueType}
                onChange={(e) => setVenueType(e.target.value)}
                placeholder="wine bar"
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-cream-300 text-plum text-sm placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
              />
            </Field>
          </div>

          <Field label="Address (optional)">
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="57 Witherspoon St, Princeton, NJ 08542"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-cream-300 text-plum text-sm placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
            />
          </Field>

          <Field label="Why it fits">
            <textarea
              value={whyItFits}
              onChange={(e) => setWhyItFits(e.target.value)}
              placeholder="A note on why this venue suits the book — shown to the club."
              rows={3}
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-cream-300 text-plum text-sm placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose resize-y"
            />
          </Field>

          <Field label="Why this edit? (optional)">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. closer to most of us, AI's pick was closed"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-cream-300 text-plum text-sm placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
            />
          </Field>

          {error ? <p className="text-sm text-rose-dark">{error}</p> : null}
        </div>

        <div className="p-5 border-t border-cream-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm text-plum-soft hover:text-plum"
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="px-5 py-2 rounded-xl bg-rose text-white text-sm hover:bg-rose-dark disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save venue"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft">
        {label}
        {required ? <span className="text-rose ml-1">*</span> : null}
      </span>
      {children}
    </label>
  );
}

// ─── Discussion questions editor ──────────────────────────────────────────
// Plain-text editor that round-trips to the ChapterQuestions[] shape. Format:
//
//   Chapter 1: The Letter
//   - Why does Henry remain at the college?
//   - What does Richard see in this group?
//
//   Chapter 2
//   - First question
//   - Second question
//
// Leading bullets (- * • ◆) and numbered prefixes (1. / 1)) are stripped.

function serializeQuestions(chapters: ChapterQuestions[]): string {
  return chapters
    .map((c) => {
      const header = c.title ? `Chapter ${c.number}: ${c.title}` : `Chapter ${c.number}`;
      const qs = c.questions.map((q) => `- ${q}`).join("\n");
      return qs ? `${header}\n${qs}` : header;
    })
    .join("\n\n");
}

function parseQuestionsText(text: string): ChapterQuestions[] {
  const chapters: ChapterQuestions[] = [];
  let current: ChapterQuestions | null = null;
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;

    const header = line.match(/^chapter\s+(\d+)\s*[:\-–—]?\s*(.*)$/i);
    if (header) {
      if (current) chapters.push(current);
      const num = parseInt(header[1] ?? "0", 10);
      const title = (header[2] ?? "").trim() || null;
      current = { number: num, title, questions: [] };
      continue;
    }

    // Strip bullets and numeric prefixes so members can use whatever style.
    const cleaned = line.replace(/^[-*•◆▸▹]+\s*/, "").replace(/^\d+[.)]\s*/, "").trim();
    if (!cleaned) continue;

    if (!current) {
      current = { number: 1, title: null, questions: [] };
    }
    current.questions.push(cleaned);
  }
  if (current) chapters.push(current);
  return chapters;
}

// ─── Themes editor ─────────────────────────────────────────────────────────
// Variable-length list of themes (Claude generates 2 by default; members can
// add more). Each theme has a short evocative title + a paragraph on how
// the book treats it. Minimum one theme.

const THEME_NUMERALS = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

function ThemesOverrideEditor({
  bookId,
  initial,
  onClose,
  onSaved,
}: CustomEditorProps<Theme[]>) {
  const seed: Theme[] =
    initial && initial.length > 0
      ? initial.map((t) => ({ title: t.title ?? "", description: t.description ?? "" }))
      : [
          { title: "", description: "" },
          { title: "", description: "" },
        ];
  const [themes, setThemes] = useState<Theme[]>(seed);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  function setTheme(i: number, patch: Partial<Theme>) {
    setThemes((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }
  function addTheme() {
    setThemes((prev) => [...prev, { title: "", description: "" }]);
  }
  function removeTheme(i: number) {
    setThemes((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  const save = useMutation({
    mutationFn: () => {
      const cleaned: Theme[] = themes
        .map((t) => ({ title: t.title.trim(), description: t.description.trim() }))
        .filter((t) => t.title || t.description);
      if (cleaned.length === 0) {
        throw new Error("Add at least one theme.");
      }
      if (cleaned.some((t) => !t.title || !t.description)) {
        throw new Error("Every theme needs both a title and a description.");
      }
      return api.post(`/annotations/${bookId}/themes/override`, {
        data: cleaned,
        reason: reason.trim() || null,
      });
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 bg-plum/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-cream-50 max-w-2xl w-full max-h-[90vh] flex flex-col border border-rule">
        <div className="flex items-center justify-between p-5 border-b border-rule">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-gold mb-0.5">
              Editing
            </div>
            <h3 className="font-serif text-xl text-plum">Themes</h3>
          </div>
          <button
            onClick={onClose}
            className="text-plum-soft hover:text-rose p-2 -mr-2 hover:bg-cream-100"
            aria-label="Close editor"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="p-5 flex flex-col gap-5 overflow-y-auto">
          <p className="text-sm text-plum-soft leading-relaxed">
            Short evocative titles, each followed by a few sentences on how the book treats it.
            Claude generates two — add more if the conversation calls for it.
          </p>

          {themes.map((t, i) => (
            <div key={i} className="flex flex-col gap-3 pb-4 border-b border-rule last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="text-[0.65rem] uppercase tracking-[0.22em] text-champagne font-mono">
                  Theme {THEME_NUMERALS[i] ?? String(i + 1)}
                </div>
                {themes.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeTheme(i)}
                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-hot-rose hover:text-bordeaux-deep px-2 py-1"
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              <Field label="Title">
                <input
                  type="text"
                  value={t.title}
                  onChange={(e) => {
                    setTheme(i, { title: e.target.value });
                    setError(null);
                  }}
                  placeholder="e.g. The cost of ambition"
                  className="w-full px-4 py-2.5 bg-white border border-rule text-ink text-sm placeholder:text-ink-mute placeholder:italic placeholder:font-serif focus:outline-none focus:border-rose"
                />
              </Field>
              <Field label="How the book treats it">
                <textarea
                  value={t.description}
                  onChange={(e) => {
                    setTheme(i, { description: e.target.value });
                    setError(null);
                  }}
                  rows={4}
                  placeholder="3–4 sentences. Specific to the book — plot points, characters by name — not a Wikipedia summary."
                  className="w-full px-4 py-2.5 bg-white border border-rule text-ink text-sm placeholder:text-ink-mute placeholder:italic placeholder:font-serif resize-y focus:outline-none focus:border-rose"
                />
              </Field>
            </div>
          ))}

          <button
            type="button"
            onClick={addTheme}
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-bordeaux border border-bordeaux px-3 py-2 hover:bg-bordeaux hover:text-rose-paper transition-colors w-fit"
          >
            <span className="font-serif italic text-base leading-none translate-y-[-1px]">+</span>
            Add a theme
          </button>

          <Field label="Why this edit? (optional)">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. added a third we kept coming back to"
              className="w-full px-4 py-2.5 bg-white border border-rule text-ink text-sm placeholder:text-ink-mute placeholder:italic placeholder:font-serif focus:outline-none focus:border-rose"
            />
          </Field>

          {error ? <p className="text-sm text-hot-rose">{error}</p> : null}
        </div>

        <div className="p-5 border-t border-rule flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm text-plum-soft hover:text-plum"
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="px-5 py-2 bg-rose text-white text-sm hover:bg-rose-dark disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save themes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuestionsOverrideEditor({
  bookId,
  initial,
  onClose,
  onSaved,
}: CustomEditorProps<ChapterQuestions[]>) {
  const [text, setText] = useState(() => (initial ? serializeQuestions(initial) : ""));
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const parsed = parseQuestionsText(text);
      if (parsed.length === 0) {
        throw new Error("Add at least one chapter with one question.");
      }
      const bad = parsed.find((c) => !c.number || c.number < 1);
      if (bad) {
        throw new Error("Every chapter needs a number — e.g. 'Chapter 3' or 'Chapter 3: Title'.");
      }
      return api.post(`/annotations/${bookId}/questions/override`, {
        data: parsed,
        reason: reason.trim() || null,
      });
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 bg-plum/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-cream-50 max-w-3xl w-full max-h-[90vh] flex flex-col border border-rule">
        <div className="flex items-center justify-between p-5 border-b border-rule">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-gold mb-0.5">
              Editing
            </div>
            <h3 className="font-serif text-xl text-plum">Discussion questions</h3>
          </div>
          <button
            onClick={onClose}
            className="text-plum-soft hover:text-rose p-2 -mr-2 hover:bg-cream-100"
            aria-label="Close editor"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4 overflow-y-auto">
          <div className="text-sm text-plum-soft leading-relaxed">
            <p className="mb-2">
              One chapter per heading, one question per line. The format is forgiving:
            </p>
            <pre className="bg-rose-paper-2/50 border border-rule p-3 text-[12px] font-mono leading-snug overflow-x-auto text-ink whitespace-pre">{`Chapter 1: The Letter
- Why does Henry remain at the college?
- What does Richard see in this group?

Chapter 2
- First question
- Second question`}</pre>
            <p className="mt-2 text-xs text-plum-mute">
              Lines starting with <code>-</code>, <code>*</code>, <code>•</code>, or <code>1.</code> all read as questions — use whatever's natural.
            </p>
          </div>

          <Field label="Questions">
            <textarea
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setError(null);
              }}
              spellCheck
              rows={18}
              className="w-full px-4 py-3 bg-white border border-rule text-ink text-[15px] font-sans leading-relaxed resize-y focus:outline-none focus:border-rose"
              placeholder={"Chapter 1: ...\n- a question\n- another question"}
            />
          </Field>

          <Field label="Why this edit? (optional)">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. trimmed to the questions we actually want to discuss"
              className="w-full px-4 py-2.5 bg-white border border-rule text-ink text-sm placeholder:text-ink-mute placeholder:italic placeholder:font-serif focus:outline-none focus:border-rose"
            />
          </Field>

          {error ? <p className="text-sm text-hot-rose">{error}</p> : null}
        </div>

        <div className="p-5 border-t border-rule flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm text-plum-soft hover:text-plum"
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="px-5 py-2 bg-rose text-white text-sm hover:bg-rose-dark disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save questions"}
          </button>
        </div>
      </div>
    </div>
  );
}
